import { RautReportIndication } from 'features/indicationTriage'
import { useGeckoLocale } from 'features/locale'
import { SavedIndicationGraph } from 'features/reporting/types'
import { useRolodexFileLSBulk, useRolodexSignedURLs } from 'features/rolodex'
import { getRenderedVisualizationPath } from 'pages/reporting/reports/raut/sections/subSections/indicationDetailsComponents/LoadSavedIndicationGraphs'
import { useMemo } from 'react'
import { GeckoMeasurementSystem } from 'types'

/**
 * Hook to fetch rendered visualization images for indications.
 *
 * Checks for auto-generated visualizations stored directly on indication entries
 * at graphs/rendered-visualization-metric.png or graphs/rendered-visualization-imperial.png.
 *
 * @param indications - List of indications to fetch images for
 * @param savedIndicationGraphs - Deprecated: kept for backwards compatibility
 * @param metadataEntryId - Deprecated: kept for backwards compatibility
 * @returns indicationsWithImages - Indications that have rendered images available
 * @returns renderedImageUrls - Map of indication ID to signed image URL
 * @returns isLoading - Whether the hook is still loading data
 */
export function useIndicationsWithImages(
  indications: RautReportIndication[],
  savedIndicationGraphs: Record<string, SavedIndicationGraph> | undefined,
  metadataEntryId: string
) {
  // Use these in void expressions to suppress unused warnings while keeping them for backwards compatibility
  void savedIndicationGraphs
  void metadataEntryId
  const { measurementSystem } = useGeckoLocale()
  const isMetric = measurementSystem === GeckoMeasurementSystem.Metric
  const visualizationPath = getRenderedVisualizationPath(isMetric)

  // Build queries to check for auto-generated visualizations on each indication entry
  // Using useRolodexFileLSBulk to check file existence
  // Query for the exact path based on current unit system
  const fileListQueries = useMemo(() => {
    if (!indications || indications.length === 0) return undefined

    return indications.map((indication) => ({
      entry_id: indication.id,
      path: visualizationPath, // Use exact path based on metric/imperial setting
    }))
  }, [indications, visualizationPath])

  // Check which indication entries have visualization files
  const {
    fileRefs: visualizationFileRefs,
    isLoading: isLoadingFileList,
    hasEverLoaded: hasEverLoadedFileList,
  } = useRolodexFileLSBulk(fileListQueries)

  // Build a set of indication IDs that have visualization files
  const indicationIdsWithVisualization = useMemo(() => {
    const ids = new Set<string>()
    visualizationFileRefs.forEach((ref) => {
      if (ref.entry_id) {
        ids.add(ref.entry_id)
      }
    })
    return ids
  }, [visualizationFileRefs])

  // Now get signed URLs only for indications that have files
  // This is more efficient than trying to get URLs for all indications
  const signedUrlQueries = useMemo(() => {
    if (indicationIdsWithVisualization.size === 0) return undefined

    return Array.from(indicationIdsWithVisualization).map((indicationId) => ({
      entry_id: indicationId,
      path: visualizationPath,
    }))
  }, [indicationIdsWithVisualization, visualizationPath])

  const { results: signedUrlResults, isLoading: isLoadingSignedUrls } =
    useRolodexSignedURLs(signedUrlQueries)

  // Build the rendered image URLs map
  const renderedImageUrls = useMemo(() => {
    const urls: Record<string, string> = {}

    signedUrlResults.forEach((result) => {
      if (result.signed_url && result.entry_id) {
        urls[result.entry_id] = result.signed_url
      }
    })

    return urls
  }, [signedUrlResults])

  // Determine if we're still loading
  const isLoading =
    isLoadingFileList ||
    !hasEverLoadedFileList ||
    (indicationIdsWithVisualization.size > 0 && isLoadingSignedUrls)

  // Filter to only include indications that have rendered images
  // Use the file list check for counting, not the signed URLs
  const indicationsWithImages = useMemo(() => {
    if (!indications) return []
    return indications.filter((indication) =>
      indicationIdsWithVisualization.has(indication.id)
    )
  }, [indications, indicationIdsWithVisualization])

  return {
    indicationsWithImages,
    renderedImageUrls,
    isLoading,
  }
}
